import { createPortal, useCallback, useEffect, useMemo, useRef, useState } from 'preact/compat'
import Icon from 'snippets/icon/icon'
import { WishlistCreateRow } from './WishlistCreateRow'
import { WishlistGuestPrompt } from './WishlistGuestPrompt'
import { WishlistListRow, WishlistRowSkeleton } from './WishlistListRow'
import { WishlistProductCard } from './WishlistProductCard'
import { useWishlistDrawer } from './useWishlistDrawer'
import { rememberWishlistLoginReturn } from '@/components/helpers/wishlist-login-return'

const FOCUSABLE_SELECTOR = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])'
].join(',')

const SKELETON_ROWS = 4

/** Drawer shell: portal, backdrop, focus trap and scroll lock. */
export const WishlistDrawer = (props) => {
  const [isOpen, setIsOpen] = useState(false)
  const [hasOpenedOnce, setHasOpenedOnce] = useState(false)
  const [signInOverlay, setSignInOverlay] = useState(false)

  const triggerRef = useRef(null)
  const panelRef = useRef(null)
  const closeButtonRef = useRef(null)
  const returnFocusRef = useRef(null)
  const wasOpenRef = useRef(false)

  const productTitle = props.dataProductTitle || ''
  const isLoggedIn = props.dataLoggedIn === 'true'

  const variants = useMemo(() => {
    try {
      return JSON.parse(props.dataVariants || '[]')
    } catch (error) {
      console.error('Failed to parse wishlist drawer variants:', error)
      return []
    }
  }, [props.dataVariants])

  const drawer = useWishlistDrawer({
    isOpen,
    hasOpenedOnce,
    isLoggedIn,
    productImage: props.dataProductImage,
    productId: props.dataProduct,
    variants,
    fallbackVariantId: props.dataSelectedVariant
  })

  const handleOpen = useCallback(() => {
    returnFocusRef.current = document.activeElement
    setIsOpen(true)
  }, [])

  const handleClose = useCallback(() => {
    setIsOpen(false)
  }, [])

  /**
   * Opens the loyalty drawer over this one. This drawer stays mounted and open behind it, so
   * closing the loyalty drawer returns the shopper to their pending save rather than to the PDP.
   * `signInOverlay` drops this drawer below the loyalty drawer's own layer for as long as it is
   * up — the loyalty drawer lives inside the sticky header's z-40 stacking context, so it cannot
   * simply be raised above a panel portaled to the body at z-50.
   */
  const handleSignIn = useCallback(() => {
    setSignInOverlay(true)
    // So the loyalty drawer's login redirect comes back to this product rather than the profile.
    rememberWishlistLoginReturn()
    window.openLoginDrawer?.()
  }, [])

  // The loyalty drawer is a Liquid x-modal outside this tree, so its close is observed rather
  // than handled: whenever it stops being visible, this drawer takes its layer back.
  useEffect(() => {
    if (!signInOverlay) return undefined

    const modal = document.querySelector('x-modal[data-id="account"]')
    if (!modal) {
      setSignInOverlay(false)
      return undefined
    }

    const observer = new MutationObserver(() => {
      if (modal.dataset.ui !== 'visible') setSignInOverlay(false)
    })
    observer.observe(modal, { attributes: true, attributeFilter: ['data-ui'] })
    return () => observer.disconnect()
  }, [signInOverlay])

  // Gate: the list body only ever mounts after the first open, so a closed
  // drawer never renders (and never downloads) row thumbnails.
  useEffect(() => {
    if (isOpen) setHasOpenedOnce(true)
  }, [isOpen])

  // Body scroll lock, matching the existing modal-dialog/key-look-drawer convention.
  useEffect(() => {
    if (!isOpen) return undefined
    document.body.classList.add('overflow-hidden')
    return () => document.body.classList.remove('overflow-hidden')
  }, [isOpen])

  // Focus: close button on open, back to the trigger (or whatever had focus) on close.
  useEffect(() => {
    if (isOpen) {
      closeButtonRef.current?.focus()
    } else if (wasOpenRef.current) {
      const target = returnFocusRef.current || triggerRef.current
      target?.focus?.()
    }
    wasOpenRef.current = isOpen
  }, [isOpen])

  // ESC to close, Tab/Shift+Tab trapped inside the panel.
  useEffect(() => {
    if (!isOpen) return undefined

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        event.stopPropagation()
        handleClose()
        return
      }

      if (event.key !== 'Tab' || !panelRef.current) return

      const focusable = Array.from(panelRef.current.querySelectorAll(FOCUSABLE_SELECTOR))
      if (focusable.length === 0) return

      const first = focusable[0]
      const last = focusable[focusable.length - 1]
      const active = document.activeElement
      const withinPanel = panelRef.current.contains(active)

      if (event.shiftKey) {
        if (!withinPanel || active === first) {
          event.preventDefault()
          last.focus()
        }
      } else if (!withinPanel || active === last) {
        event.preventDefault()
        first.focus()
      }
    }

    document.addEventListener('keydown', handleKeyDown, true)
    return () => document.removeEventListener('keydown', handleKeyDown, true)
  }, [isOpen, handleClose])

  const renderLists = () => {
    if (drawer.isLoading) {
      return Array.from({ length: SKELETON_ROWS }, (_, index) => <WishlistRowSkeleton key={index} />)
    }

    if (drawer.loadError) {
      return (
        <div
          data-role="wishlist-drawer-load-error"
          class="flex flex-col items-start gap-3 rounded bg-primary-white p-4"
        >
          <p class="paragraph-sm">We could not load your wishlists.</p>
          <button
            type="button"
            onClick={drawer.onRetry}
            data-role="wishlist-drawer-retry"
            class="btn btn-md btn-outline-black"
          >
            Try again
          </button>
        </div>
      )
    }

    return drawer.lists.map((list) => {
      const entry = drawer.draft.entries[list.id]
      if (!entry) return null
      return (
        <WishlistListRow
          key={list.id}
          list={list}
          entry={entry}
          isActive={String(drawer.draft.activeListId) === String(list.id)}
          sizeLabel={drawer.labelFor(entry.variantId)}
          error={drawer.rowErrors[list.id]}
          disabled={drawer.isSaving}
          onToggle={drawer.onToggle}
          onActivate={drawer.onActivate}
        />
      )
    })
  }

  return (
    <>
      <button
        type="button"
        ref={triggerRef}
        onClick={handleOpen}
        aria-expanded={isOpen}
        data-role="wishlist-drawer-trigger"
        data-saved={drawer.isSaved ? 'true' : 'false'}
        class={`btn btn-md w-full gap-2 border border-solid ${
          drawer.isSaved ? 'border-[#389810] text-[#389810]' : 'btn-outline-black'
        }`}
      >
        {drawer.isSaved && <Icon id="tick" className="h-5 w-5 shrink-0" />}
        <span>{drawer.isSaved ? 'Product Saved' : 'Save to Wishlist'}</span>
      </button>

      {createPortal(
        <>
          <div
            onClick={isOpen ? handleClose : undefined}
            data-role="wishlist-drawer-backdrop"
            class={`fixed inset-0 h-screen w-screen cursor-pointer bg-black/40 transition-opacity duration-300 ${
              signInOverlay ? 'z-[19]' : 'z-[49]'
            } ${isOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}
          />

          {/*
            Bottom sheet below md, right-hand drawer at md and above. `md` is 1024px in this theme,
            so the sheet covers phones and tablets. Both axes are reset per breakpoint: leaving
            translate-y set at md would push the desktop drawer off the bottom of the viewport.
          */}
          <div
            ref={panelRef}
            role="dialog"
            aria-modal="true"
            aria-labelledby="wishlist-drawer-title"
            data-role="wishlist-drawer-panel"
            class={`fixed flex flex-col bg-primary-grey transition-transform duration-300
              ${signInOverlay ? 'z-20' : 'z-50'}
              inset-x-0 bottom-0 top-auto max-h-[85dvh] w-full rounded-t-xl
              md:inset-x-auto md:right-0 md:top-0 md:max-h-none md:w-125 md:rounded-t-none ${
              isOpen
                ? 'translate-y-0 md:translate-x-0 md:translate-y-0'
                : 'invisible pointer-events-none translate-y-full md:translate-x-full md:translate-y-0'
            }`}
          >
            <div class="flex w-full flex-row items-center justify-between border-b p-4">
              <h4 id="wishlist-drawer-title" class="h4">
              Save to Wishlist
              </h4>
              <button
                type="button"
                ref={closeButtonRef}
                onClick={handleClose}
                tabIndex={isOpen ? 0 : -1}
                data-role="wishlist-drawer-close"
                class="-m-4 p-4"
              >
                <Icon id="close" className="w-5 h-5" />
              </button>
            </div>

            {hasOpenedOnce && (
              <div data-role="wishlist-drawer-body" class="no-scrollbar flex flex-1 flex-col gap-4 overflow-y-auto p-4">
                <WishlistProductCard
                  title={productTitle}
                  colour={props.dataProductColour}
                  image={props.dataProductImage}
                  badge={props.dataBadge}
                  badgeBg={props.dataBadgeBg}
                  badgeFg={props.dataBadgeFg}
                  variants={variants}
                  activeEntry={drawer.activeEntry}
                  activeListId={drawer.draft.activeListId}
                  displayVariantId={drawer.selectedVariantId}
                  productPrice={props.dataProductPrice}
                  productId={props.dataProduct}
                  sizeType={props.dataSizeType}
                  sizeLayout={props.dataSizeLayout}
                  isReadOnly={!isLoggedIn || !!drawer.loadError}
                  onSize={drawer.onSize}
                />

                {isLoggedIn ? (
                  <>
                    {renderLists()}
                    <WishlistCreateRow
                      isPrimary={!drawer.isLoading && !drawer.loadError && drawer.lists.length === 0}
                      isSaving={drawer.isSaving}
                      error={drawer.createError}
                      onCreate={drawer.onCreate}
                    />
                  </>
                ) : (
                  <WishlistGuestPrompt onSignIn={handleSignIn} />
                )}
              </div>
            )}

            {hasOpenedOnce && isLoggedIn && (
              <div class="border-t bg-primary-grey p-4">
                <button
                  type="button"
                  disabled={!drawer.canSubmit}
                  onClick={() => drawer.onSubmit(handleClose)}
                  data-role="wishlist-drawer-submit"
                  class="btn btn-md btn-solid-black w-full"
                >
                  Save to Wishlist
                </button>
              </div>
            )}
          </div>
        </>,
        document.body
      )}
    </>
  )
}
